// src/lib/sample-data/spaces/spaces-core.ts
import type { Space, CourtSportType, ActivityAttributeType, MedicalAttributeType, AvailabilitySlot } from '@/lib/types';
import { format } from 'date-fns';
import { SAMPLE_DATA_CONFIG, DEFAULT_ORGANIZATION } from '../config';
import { defaultOrganizationId } from '../organizations';

// Fixed ID mapping from textual IDs to numeric IDs
export const spaceIdMapping: Record<string, string> = {
  'T1': '4051728396',
  'T2': '5162839407',
  'T3': '6273940518',
  'T4': '7384051629',
  'T5': '8495162730',
  'P1': '9506273841',
  'P2': '1617384952',
  'P3': '2728495063',
  'PA': '3839506174',
  'PB1': '4940617285',
  'B1': '5051728396',
  'BD1': '6051839407',
  'Z': '7162940518',
  'MR1': '8273051629',
  'CW1': '9384162740',
  'AP1': '1495273851',
  'FIT1': '2506384962',
  'YOG1': '3617495073',
  'PIL1': '4728506184',
  'MAS1': '5839617295',
  'PHY1': '6940728306',
  'CHI1': '7051839417',
  // New spaces for Verbier
  'PR_T1': '8162940518',
  'PR_T2': '9273051629',
  'PR_T3': '1384162740',
  'PR_T4': '2495273851',
  'PR_T5': '3506384962',
  // New spaces for Urban London
  'UL_T1': '4617495073',
  'UL_T2': '5728506184',
  'UL_P1': '6839617295',
  'UL_FIT': '7940728306',
  // New spaces for Urban New York
  'UN_T1': '8051839417',
  'UN_T2': '9162940528',
  'UN_PB1': '1273051639',
  'UN_PB2': '2384162740',
  'UN_ROOF': '3495273851',
  // New spaces for Urban Tokyo
  'UT_T1': '4506384962',
  'UT_T2': '5617495073',
  'UT_B1': '6728506184',
  'UT_ZEN': '7839617295',
  // New spaces for All England Lawn Tennis
  'AE_C1': '8940728306'
};

export const allDaysAvailability: AvailabilitySlot[] = [
  { days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], startTime: '08:00', endTime: '22:00', price: 20 },
];

export interface RawSpaceData extends Omit<Space, 'id' | 'organizationId' | 'dateCreated' | 'dateUpdated' | 'clubId' | 'tenantId'> {
  id: string; // Temporary textual ID for mapping
  dateCreated?: string;
  dateUpdated?: string;
  clubId?: string; // Temporary club reference
}

// Helper function to assign club based on sport type or space type
export const getClubIdForSport = (courtType?: CourtSportType, spaceId?: string, activityAttribute?: ActivityAttributeType, medicalAttribute?: MedicalAttributeType): string => {
  // Handle Medical space type
  if (medicalAttribute) {
    switch (medicalAttribute) {
      case 'Massage':
      case 'Physiotherapy':
      case 'Chiropractor':
        return '3748591620'; // Multi-Sport Complex (wellness center)
      default:
        return SAMPLE_DATA_CONFIG.defaultClubId;
    }
  }
  
  // Handle Activity space type
  if (activityAttribute) {
    switch (activityAttribute) {
      case 'Climbing Wall':
      case 'American Pool':
      case 'Fitness':
        if (spaceId === 'UL_FIT') return '2039485761'; // Urban London
        return '3748591620'; // Multi-Sport Complex
      case 'Yoga':
      case 'Pilates':
        return '3748591620'; // Multi-Sport Complex
      default:
        return SAMPLE_DATA_CONFIG.defaultClubId;
    }
  }
  
  // Handle specific space IDs for new clubs
  if (spaceId?.startsWith('PR_')) return '1928374650'; // Paris Racing
  if (spaceId?.startsWith('UL_')) return '2039485761'; // Urban London
  if (spaceId?.startsWith('UN_')) return '3140596872'; // Urban New York
  if (spaceId?.startsWith('UT_')) return '4251607983'; // Urban Tokyo
  if (spaceId?.startsWith('AE_')) return '5362719094'; // All England Lawn Tennis
  
  // Handle Court space type
  switch (courtType) {
    case 'Padel':
      if (spaceId === 'UL_P1') return '2039485761'; // Urban London
      return '7384629150'; // Global Padel Club
    case 'Tennis':
      if (spaceId === 'T1' || spaceId === 'T2' || spaceId === 'T5') {
        return SAMPLE_DATA_CONFIG.defaultClubId; // Topspin Tennis Club
      }
      if (spaceId?.startsWith('PR_')) return '1928374650'; // Paris Racing
      if (spaceId?.startsWith('UL_')) return '2039485761'; // Urban London
      if (spaceId?.startsWith('UN_')) return '3140596872'; // Urban New York
      if (spaceId?.startsWith('UT_')) return '4251607983'; // Urban Tokyo
      if (spaceId?.startsWith('AE_')) return '5362719094'; // All England Lawn Tennis
      return '4629385170'; // Elite Tennis Academy
    case 'Pickleball':
      if (spaceId === 'PB1') {
        return SAMPLE_DATA_CONFIG.defaultClubId; // Topspin Tennis Club
      }
      if (spaceId?.startsWith('UN_')) return '3140596872'; // Urban New York
      return '3748591620'; // Multi-Sport Complex
    case 'Badminton':
      if (spaceId === 'BD1') {
        return SAMPLE_DATA_CONFIG.defaultClubId; // Topspin Tennis Club
      }
      if (spaceId?.startsWith('UT_')) return '4251607983'; // Urban Tokyo
      return '3748591620'; // Multi-Sport Complex
    default:
      return SAMPLE_DATA_CONFIG.defaultClubId; // Topspin Tennis Club (default)
  }
};

// Helper function to convert raw space data to final Space format
export const convertRawSpaceData = (rawSpaces: RawSpaceData[]): Space[] => {
  return rawSpaces.map((s) => {
    const newId = spaceIdMapping[s.id]; // Use fixed ID from mapping
    const businessId = s.id; // Store the original textual ID as businessId
    const nowStr = format(new Date(), 'yyyy-MM-dd');
    
    // Business Logic: Organization → Club → Space hierarchy
    // Every space MUST belong to a club (clubId is required)
    const assignedClubId = s.clubId || SAMPLE_DATA_CONFIG.defaultClubId;
    
    return {
      ...s,
      id: newId,
      businessId,
      organizationId: defaultOrganizationId,
      clubId: assignedClubId,
      tenantId: DEFAULT_ORGANIZATION.tenantId,
      dateCreated: s.dateCreated || nowStr,
      dateUpdated: s.dateUpdated || nowStr,
    };
  });
}; 